import type {
  CreateSlaPolicyRequest,
  SlaClockKey,
  SlaEntityType,
  SlaEscalationLevel,
  SlaEscalationRow,
  SlaPolicyResponse,
  SlaPolicyScope,
  SlaPriorityMatch,
  SlaTargetRow,
  TicketStatusCategory,
  UpdateSlaPolicyRequest,
} from '@troia-v3/types';
import { TICKET_STATUS_CATEGORIES } from '@troia-v3/types';

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/** Espelho de `slaTargetRowSchema.targetMs: z.number().int().min(60_000)` (B2). */
export const SLA_TARGET_MIN_MS = MINUTE_MS;

export const CLOCK_KEY_LABELS: Record<SlaClockKey, string> = {
  first_response: 'Primeira resposta',
  resolution: 'Resolução',
};

export const PRIORITY_MATCH_LABELS: Record<SlaPriorityMatch, string> = {
  '*': 'Todas as prioridades',
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente',
};

export const ESCALATION_LEVEL_LABELS: Record<SlaEscalationLevel, string> = {
  warning: 'Aviso',
  breach: 'Violação',
  l2: 'Escalação (N2)',
};

/** Opções de `pausesOn` — rótulos vêm do catálogo, não de literais soltos. */
export const STATUS_CATEGORY_OPTIONS: Array<{ value: TicketStatusCategory; label: string }> =
  (Object.keys(TICKET_STATUS_CATEGORIES) as TicketStatusCategory[]).map((key) => ({
    value: key,
    label: TICKET_STATUS_CATEGORIES[key].label,
  }));

export type DurationUnit = 'minutes' | 'hours' | 'days';
export interface DurationDraft {
  value: number;
  unit: DurationUnit;
}

const UNIT_MS: Record<DurationUnit, number> = { minutes: MINUTE_MS, hours: HOUR_MS, days: DAY_MS };
const UNIT_SHORT: Record<DurationUnit, string> = { minutes: 'min', hours: 'h', days: 'd' };

/**
 * Maior unidade que divide o valor sem resto — 900000 vira `15 minutos`,
 * 86400000 vira `1 dia`. O campo de formulário é (número + unidade) porque
 * `targetMs` cru em milissegundos é impossível de digitar sem errar uma casa,
 * e um zero a mais num alvo de SLA passa despercebido na revisão.
 */
export function msToDuration(ms: number): DurationDraft {
  if (ms > 0 && ms % DAY_MS === 0) return { value: ms / DAY_MS, unit: 'days' };
  if (ms > 0 && ms % HOUR_MS === 0) return { value: ms / HOUR_MS, unit: 'hours' };
  return { value: Math.round(ms / MINUTE_MS), unit: 'minutes' };
}

export function durationToMs(draft: DurationDraft): number {
  return Math.round(draft.value) * UNIT_MS[draft.unit];
}

/**
 * Valor numérico de `ms` NUMA unidade ESCOLHIDA — não a "melhor" que
 * `msToDuration` escolheria. Usado só pelo revert de blur do campo de prazo
 * (`SlaTargetDurationField`, Rodada 2): reverter para o último valor válido
 * precisa mostrar esse valor na unidade que o usuário JÁ tinha selecionado,
 * nunca na que `msToDuration` escolheria por conta própria (86400000ms é
 * "1 dia" pelo best-fit, mas o usuário podia estar editando em "horas" —
 * usar `msToDuration` ali trocaria a unidade visível sem o usuário pedir,
 * reintroduzindo por outra porta o mesmo tipo de flip silencioso da Rodada
 * 1/2). Arredondado a 2 casas — puramente de EXIBIÇÃO, nunca é o valor que
 * vira `targetMs` de novo (o commit sempre passa por `durationToMs`, nunca
 * por esta função): o caso em que a divisão não é exata só existe se o
 * usuário trocar de unidade com o campo inválido/em branco (sem digitar
 * nada) — a duração committada continua correta, só a CASA decimal do
 * número mostrado é que existiria sem o arredondamento.
 */
export function msToUnitValue(ms: number, unit: DurationUnit): number {
  return Math.round((ms / UNIT_MS[unit]) * 100) / 100;
}

/** `"15 min úteis"` / `"24 h"` — o sufixo diz a BASE de tempo, não decoração. */
export function formatTargetMs(ms: number, useBusinessHours: boolean): string {
  const { value, unit } = msToDuration(ms);
  return `${value} ${UNIT_SHORT[unit]}${useBusinessHours ? ' úteis' : ''}`;
}

/**
 * Estado local do campo "Prazo de [valor] [unidade]" (`SlaTargetDurationField`,
 * `SlaTargetsTab`, C18). `rawValue` é STRING (não `number`) de propósito: é o
 * texto exatamente como o usuário digitou (inclui `''`, `'0'`, `'05'`,
 * qualquer estado transitório) — nunca uma versão já normalizada.
 *
 * Existe pra resolver dois achados de revisão da mesma classe de bug:
 * Rodada 1: apagar o campo (`Number('')===0`) gravava `targetMs:0`, e
 * `msToDuration(0)` SEMPRE cai em minutos (0 é divisível por qualquer
 * unidade) — o Select de unidade mudava sozinho. Rodada 2: o remendo só
 * cobria string vazia; digitar "0" EXPLICITAMENTE (`Number('0')===0`) caía
 * no mesmo ramo numérico e reproduzia o flip do mesmo jeito — sequência real:
 * "24" em horas → seleciona tudo → digita "0" → digita "5" → gravava 5
 * MINUTOS em vez de 5 horas, sem erro de validação (300000ms ≥ o piso de 1
 * minuto). A causa raiz nas duas rodadas é a mesma: a unidade era DERIVADA
 * de `targetMs` a cada render (`msToDuration(row.targetMs)`), então qualquer
 * valor que passasse por 0 — vazio OU "0" — contaminava a unidade do PRÓXIMO
 * commit. A correção definitiva não é filtrar mais um caso de string: é
 * parar de derivar a unidade do valor commitado. `unit` aqui SÓ muda por
 * escolha explícita do usuário no Select (`applyDurationFieldUnitChange`).
 */
export interface DurationFieldState {
  rawValue: string;
  unit: DurationUnit;
}

/** Resultado de uma transição do campo: o próximo estado local e o que (se
 *  algo) deveria subir pro rascunho pai. */
export interface DurationFieldTransition {
  next: DurationFieldState;
  /** `null` = o estado atual não é um número válido (>0) — não commita nada,
   *  o pai continua com o último `targetMs` válido. */
  commitMs: number | null;
}

function commitFor(rawValue: string, unit: DurationUnit): number | null {
  const numeric = Number(rawValue);
  if (rawValue === '' || !Number.isFinite(numeric) || numeric <= 0) return null;
  // Fracionário NÃO commita: `durationToMs` arredonda o VALOR ("0.5" viraria
  // 1 hora) enquanto o campo continuaria exibindo "0.5" — divergência
  // silenciosa entre UI e rascunho (achado da rodada 2). Meia hora se escreve
  // "30" em minutos; segurar o commit deixa o blur reverter ao último válido.
  if (!Number.isInteger(numeric)) return null;
  return durationToMs({ value: numeric, unit });
}

/**
 * Transição pura ao digitar um novo valor bruto no campo. `state.unit` NUNCA
 * é tocado aqui — só o `rawValue` muda, e o commit (se houver) usa a unidade
 * que já estava selecionada. É esta linha que fecha os dois achados: nem "0"
 * nem "" mudam `unit`, então o próximo dígito digitado continua na MESMA
 * unidade que o usuário escolheu.
 */
export function applyDurationFieldValueChange(
  state: DurationFieldState,
  raw: string,
): DurationFieldTransition {
  return { next: { ...state, rawValue: raw }, commitMs: commitFor(raw, state.unit) };
}

/**
 * Transição pura ao trocar a unidade no Select. Preserva o NÚMERO exibido
 * (mesmo comportamento que a tela sempre teve: "24" em horas vira "24" em
 * dias, reinterpretado) — só reinterpreta a unidade.
 */
export function applyDurationFieldUnitChange(
  state: DurationFieldState,
  unit: DurationUnit,
): DurationFieldTransition {
  return { next: { ...state, unit }, commitMs: commitFor(state.rawValue, unit) };
}

/**
 * Nova ordem dos ids depois de arrastar `draggedId` para a posição de
 * `targetId`. `null` = arrasto sem efeito — o chamador não dispara requisição.
 *
 * Mesma aritmética (e mesma matriz de teste) de `reorderStageIds`
 * (`helpdesk/pipelines/components/StagesManager.tsx`, C7). Duplicada de
 * propósito: promover a função a primitivo compartilhado é mudança
 * cross-cutting e não é escopo deste anexo.
 *
 * `policies` PRECISA ser o conjunto completo de políticas ATIVAS do
 * `entityType` sendo exibido (a listagem inteira, não uma página) — é o
 * array que o chamador (C17) manda direto para `useReorderSlaPolicies`, e o
 * backend recusa com 400 qualquer lista que não seja esse conjunto completo
 * (ver `useSlaPolicies.ts`).
 */
export function reorderPolicyIds(
  policies: ReadonlyArray<{ id: string }>,
  draggedId: string,
  targetId: string,
): string[] | null {
  if (!draggedId || draggedId === targetId) return null;
  const draggedIdx = policies.findIndex((p) => p.id === draggedId);
  const targetIdx = policies.findIndex((p) => p.id === targetId);
  if (draggedIdx === -1 || targetIdx === -1) return null;

  const ids = policies.map((p) => p.id);
  const [moved] = ids.splice(draggedIdx, 1);
  if (moved === undefined) return null;
  ids.splice(targetIdx, 0, moved);
  return ids;
}

/**
 * A política padrão tem de ficar em ÚLTIMO na ordem de avaliação.
 *
 * Espelho do guard do `SlaPoliciesService.reorder` (B3), que recusa a
 * reordenação com 409 (ConflictError) quando a padrão não é a última — ela é o piso do
 * casamento: qualquer política avaliada DEPOIS dela seria inalcançável.
 * Checar aqui evita mandar ao servidor um arrasto que já se sabe inválido, e
 * é o mesmo raciocínio do `buildPipelineDeleteConfirm` (C8): perguntar antes
 * de agir em vez de agir e ler o erro.
 */
export function isDefaultLast(
  policies: ReadonlyArray<{ id: string; isDefault: boolean }>,
  orderedIds: string[],
): boolean {
  const defaultPolicy = policies.find((p) => p.isDefault);
  if (!defaultPolicy) return true;             // sem padrão, nada a proteger
  if (!orderedIds.includes(defaultPolicy.id)) return true;
  return orderedIds[orderedIds.length - 1] === defaultPolicy.id;
}

/**
 * Recorta, de uma lista qualquer de políticas exibidas (mistura `entityType`,
 * inclui inativas), o subconjunto exato que o backend valida contra numa
 * reordenação: políticas ATIVAS do MESMO `entityType` do item sendo
 * arrastado — o universo de `findActiveForCompany` (`SlaPoliciesService.reorder`,
 * B3). Extraído de `handleDrop` (C17, Rodada 1 de revisão) para ganhar
 * cobertura de teste isolada: a lógica de "que subconjunto participa do
 * arrasto" regride em silêncio — a UI só quebra quando existe uma 2ª
 * política inativa, ou uma de outro `entityType`, cenário que não aparece no
 * teste manual do dia a dia (hoje só existe 1 política de chamado).
 */
export function buildReorderScope<
  T extends { status: 'active' | 'inactive'; entityType: SlaEntityType },
>(items: readonly T[], entityType: SlaEntityType): T[] {
  return items.filter((p) => p.status === 'active' && p.entityType === entityType);
}

/**
 * Ordena políticas na ORDEM DE AVALIAÇÃO do motor: `(order asc, id asc)` —
 * o MESMO desempate do backend, `.sort({ order: 1, _id: 1 })` em
 * `SlaPoliciesRepository.findActiveForCompany`/`.reorder`
 * (`TROIA-V3-BACKEND/src/modules/sla-policies/repository.ts:256,321`).
 * `id` aqui é a string do `ObjectId` (`mapToResponse`) — comparar como
 * string casa com a ordem do `_id` nativo porque os 24 caracteres hex têm
 * o MESMO comprimento e o Mongo já ordena `ObjectId` por bytes: comparação
 * lexicográfica de dois hex strings de igual tamanho reproduz a ordem
 * numérica byte a byte.
 *
 * Achado da Rodada 1 de conserto (C21): nem `useSlaPolicies` nem
 * `slaPoliciesApi.list` mandavam `sort` nenhum pro `GET /api/sla-policies`,
 * e o `GenericRepository.buildSort` do backend defaulta pra
 * `{ createdAt: -1 }` sem um `sort` explícito — a listagem chegava na ordem
 * de CRIAÇÃO (mais recente primeiro), não na de avaliação. Dois efeitos:
 * (a) `SlaPoliciesPage` (C17) numerava errado sempre que `dragOrder` era
 * `null` (todo carregamento inicial, toda busca), porque
 * `rankPoliciesForDisplay` abaixo NUMERA A SEQUÊNCIA DE ENTRADA — ela não
 * reordena, só conta; (b) o `.find()` de `linkedSlaPolicy` no drawer de
 * pipelines (C21) podia devolver a política MAIS RECENTE em vez da que
 * `resolvePolicy` (backend) escolheria de fato — trocar o vínculo a partir
 * dali podia deixar o mesmo funil em DUAS políticas.
 *
 * Fix na CAMADA DE DADOS, um lugar só: `useSlaPolicies` (hook consumido por
 * `SlaPoliciesPage` E `PipelinesSettingsDrawer`) aplica esta função via
 * `select` antes de entregar `data.items` — os dois consumidores herdam a
 * correção sem precisar de mudança própria.
 *
 * NÃO filtra nem separa por `status`/`entityType` — ordena o array inteiro
 * como veio. Duas razões: `order` é uma sequência INDEPENDENTE por
 * `entityType` (o motor cascateia por tipo; `SlaPoliciesService.reorder`
 * recusa misturar tipos no mesmo payload), então o resultado pode
 * INTERCALAR chamado/conversa quando os dois existem — inofensivo, porque
 * `rankPoliciesForDisplay` conta com um contador PRÓPRIO por `entityType`
 * enquanto itera, e a intercalação entre tipos não perturba a sequência
 * DENTRO de um tipo. E uma política INATIVA nunca passa pelo `reorder` do
 * backend depois de desativada — seu `order` fica CONGELADO no valor de
 * quando foi ativa pela última vez; ordenar por esse mesmo campo, sem
 * tratamento especial, já a deixa na posição relativa em que estava (não
 * "manda pro fim" nem precisa de um segundo critério pra isso).
 */
export function sortPoliciesForEvaluation<T extends { id: string; order: number }>(
  items: readonly T[],
): T[] {
  return [...items].sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    if (a.id === b.id) return 0;
    return a.id < b.id ? -1 : 1;
  });
}

/**
 * Todas as políticas cujo `scope.pipelineIds` contém `pipelineId`, na MESMA
 * ordem em que vieram no array recebido — o chamador (`PipelinesSettingsDrawer`,
 * `syncSlaPolicyLink`) passa `slaPolicies` já na ordem de avaliação
 * (`sortPoliciesForEvaluation`, aplicada em `useSlaPolicies`), então o
 * PRIMEIRO item do retorno é sempre quem `resolvePolicy` (backend) escolheria
 * de fato — o resto são "perdedoras" na cascata, mas ainda assim vínculos
 * REAIS gravados no servidor.
 *
 * Existe pra dado legado (achado da Rodada 2 de conserto, C21): nada no
 * backend impede duas políticas de listarem o mesmo pipeline no escopo ao
 * mesmo tempo — `resolvePolicy` resolve isso escolhendo a primeira na ordem
 * de avaliação, mas até a Rodada 1 a UI só sabia limpar essa "vencedora"
 * (via `.find()`), deixando a(s) "perdedora(s)" penduradas indefinidamente.
 * `syncSlaPolicyLink` usa esta função pra encontrar TODAS antes de trocar o
 * vínculo, não só a que o campo mostrava.
 */
export function policiesContainingPipeline<T extends { id: string; scope: SlaPolicyScope }>(
  policies: readonly T[],
  pipelineId: string,
): T[] {
  return policies.filter((p) => (p.scope.pipelineIds ?? []).includes(pipelineId));
}

/**
 * Mapa `id → posição de avaliação` (1-based), numerada DENTRO do grupo
 * (mesmo `entityType`, só políticas ATIVAS) — a cascata do motor é POR
 * `entityType` (`SlaPoliciesService.resolvePolicy`), e uma política inativa
 * nunca é avaliada. Indexar direto no array exibido (que mistura tipos e
 * inclui inativas) mente sobre a posição real: achado da Rodada 1 de revisão
 * — ativa/inativa/padrão mostrava 1/—/3 quando a cascata de fato só tem 2
 * posições (a inativa consumia um índice que não é dela).
 *
 * Política sem entrada no mapa (inativa) não é avaliada — o chamador decide
 * como exibir isso (hoje, "—" em vez de número).
 *
 * **Pré-requisito**: `items` já vem na ordem de avaliação
 * (`sortPoliciesForEvaluation`, aplicada em `useSlaPolicies`) — esta função
 * só CONTA a sequência de entrada, não reordena nada.
 */
export function rankPoliciesForDisplay(
  items: ReadonlyArray<{ id: string; status: 'active' | 'inactive'; entityType: SlaEntityType }>,
): Map<string, number> {
  const ranks = new Map<string, number>();
  const counters = new Map<SlaEntityType, number>();
  for (const item of items) {
    if (item.status !== 'active') continue;
    const next = (counters.get(item.entityType) ?? 0) + 1;
    counters.set(item.entityType, next);
    ranks.set(item.id, next);
  }
  return ranks;
}

/** Espelho de `assertUniqueTargets` (B2). `-1` = sem duplicata. */
export function findDuplicateTargetIndex(
  targets: ReadonlyArray<Pick<SlaTargetRow, 'clockKey' | 'priority'>>,
): number {
  const seen = new Set<string>();
  for (let i = 0; i < targets.length; i += 1) {
    const key = `${targets[i]!.clockKey}:${targets[i]!.priority}`;
    if (seen.has(key)) return i;
    seen.add(key);
  }
  return -1;
}

/** Espelho de `assertUniqueEscalation` (B2). `-1` = sem duplicata. */
export function findDuplicateEscalationIndex(
  rows: ReadonlyArray<Pick<SlaEscalationRow, 'clockKey' | 'level'>>,
): number {
  const seen = new Set<string>();
  for (let i = 0; i < rows.length; i += 1) {
    const key = `${rows[i]!.clockKey}:${rows[i]!.level}`;
    if (seen.has(key)) return i;
    seen.add(key);
  }
  return -1;
}

/**
 * Validação de UMA linha de alvo.
 *
 * A regra de `pausesOn` em política de CONVERSA não é preferência de UI: o
 * `superRefine` da tarefa D1 (Plano D) devolve **422** para `pausesOn`
 * não-vazio com `entityType: 'conversation'`, porque a semântica de `waiting`
 * é invertida entre as duas entidades — no chamado significa "aguardando o
 * cliente" (pausa), na conversa significa "na fila, o cliente esperando por
 * nós" (não pausa). Uma política de conversa com `pausesOn: ['waiting']`
 * congelaria o relógio de toda conversa em fila e levaria a taxa de SLA a 100%
 * para sempre. Aqui o formulário nem deixa montar o payload.
 */
export function validateTargetRow(row: SlaTargetRow, entityType: SlaEntityType): string | null {
  if (!Number.isFinite(row.targetMs) || !Number.isInteger(row.targetMs) || row.targetMs < SLA_TARGET_MIN_MS) {
    return 'O alvo deve ser de pelo menos 1 minuto.';
  }
  if (entityType === 'conversation' && row.pausesOn.length > 0) {
    return 'Política de conversa não pausa por status — a pausa é direcional. Deixe a lista vazia.';
  }
  if (row.pausesOn.length > 5) {
    return 'No máximo 5 categorias de pausa por linha.';
  }
  return null;
}

export type OffsetSense = 'before' | 'at' | 'after';

/** `offsetMs` é ASSINADO em relação ao vencimento: <0 antes, 0 no prazo, >0 depois. */
export function offsetSenseOf(offsetMs: number): OffsetSense {
  if (offsetMs < 0) return 'before';
  if (offsetMs > 0) return 'after';
  return 'at';
}

/**
 * Recompõe o valor assinado a partir do par (sentido, minutos) que o
 * formulário exibe. O usuário nunca digita o sinal — digitar "-5" num campo
 * rotulado "antes do vencimento" produziria +5 minutos DEPOIS, em silêncio.
 */
export function composeOffsetMs(sense: OffsetSense, minutes: number): number {
  const magnitude = Math.abs(Math.round(minutes)) * MINUTE_MS;
  if (sense === 'at') return 0;
  return sense === 'before' ? -magnitude : magnitude;
}

/** Magnitude em minutos de um `offsetMs` assinado — só para inicializar/reverter o campo local. */
export function offsetMsToMinutes(offsetMs: number): number {
  return Math.abs(Math.round(offsetMs / MINUTE_MS));
}

/**
 * Estado local do campo "Disparar [minutos] min [sentido]"
 * (`SlaEscalationOffsetField`, `SlaEscalationTab`, C19) — MESMO mecanismo do
 * `DurationFieldState` (`SlaTargetDurationField`, C18) e pelo mesmo motivo.
 *
 * A versão ingênua deriva `minutes`/`sense` de `row.offsetMs` a cada render
 * (`Math.abs(Math.round(offsetMs / 60_000))` + `offsetSenseOf(offsetMs)`).
 * Isso reproduz a MESMA classe de bug das rodadas 1/2 do campo de prazo, só
 * que pior: apagar o campo para redigitar manda `Number('')===0` →
 * `composeOffsetMs(sense, 0)` → `offsetMs` passa por `0` → no próximo render
 * `offsetSenseOf(0)` retorna `'at'` → o Select de sentido pula sozinho para
 * "no vencimento" **e** o próprio campo de minutos fica `disabled` (a regra
 * de UI desabilita o campo quando `sense === 'at'`) — o usuário fica
 * TRAVADO no meio de apagar-e-redigitar, sem conseguir completar o novo
 * valor. A correção é a mesma: `sense` (aqui, o equivalente a `unit`) só
 * muda por escolha explícita do usuário no Select
 * (`applyOffsetFieldSenseChange`); `rawMinutes` nunca é recomputado de
 * `offsetMs` a cada render.
 */
export interface OffsetFieldState {
  rawMinutes: string;
  sense: OffsetSense;
}

/** Resultado de uma transição do campo de escalação: o próximo estado local
 *  e o que (se algo) deveria subir pro rascunho pai. */
export interface OffsetFieldTransition {
  next: OffsetFieldState;
  /** `null` = não commita nada — o pai continua com o último `offsetMs` válido. */
  commitMs: number | null;
}

function commitOffsetFor(rawMinutes: string, sense: OffsetSense): number | null {
  // Sentido "no vencimento" ignora a magnitude — sempre commita 0, mesmo com
  // o campo de minutos vazio/inválido (ele fica desabilitado nesse sentido).
  if (sense === 'at') return 0;
  const numeric = Number(rawMinutes);
  if (rawMinutes === '' || !Number.isFinite(numeric) || numeric <= 0) return null;
  // Fracionário NÃO commita — mesma regra do `DurationFieldState` (rodada 2):
  // `composeOffsetMs` arredonda o VALOR enquanto o campo continuaria
  // exibindo o fracionário, divergência silenciosa entre UI e rascunho.
  if (!Number.isInteger(numeric)) return null;
  return composeOffsetMs(sense, numeric);
}

/**
 * Transição pura ao digitar um novo valor bruto de minutos. `state.sense`
 * NUNCA é tocado aqui — só o `rawMinutes` muda, e o commit (se houver) usa o
 * sentido que já estava selecionado.
 */
export function applyOffsetFieldMinutesChange(
  state: OffsetFieldState,
  raw: string,
): OffsetFieldTransition {
  return { next: { ...state, rawMinutes: raw }, commitMs: commitOffsetFor(raw, state.sense) };
}

/**
 * Transição pura ao trocar o sentido no Select. Preserva o NÚMERO de minutos
 * já digitado — só reinterpreta o sinal (ou zera, se o novo sentido for
 * "no vencimento").
 */
export function applyOffsetFieldSenseChange(
  state: OffsetFieldState,
  sense: OffsetSense,
): OffsetFieldTransition {
  return { next: { ...state, sense }, commitMs: commitOffsetFor(state.rawMinutes, sense) };
}

export interface SlaPolicyDraft {
  name: string;
  description: string;
  entityType: SlaEntityType;
  status: 'active' | 'inactive';
  scope: SlaPolicyScope;
  targets: SlaTargetRow[];
  escalation: SlaEscalationRow[];
  countAiAsFirstResponse: boolean;
}

/** Rascunho novo: uma linha de alvo coringa, para a política nascer válida. */
export function draftFromPolicy(
  policy: SlaPolicyResponse | null,
  entityType: SlaEntityType,
): SlaPolicyDraft {
  if (!policy) {
    return {
      name: '',
      description: '',
      entityType,
      status: 'active',
      scope: {},
      targets: [
        {
          clockKey: 'first_response',
          priority: '*',
          targetMs: 15 * MINUTE_MS,
          useBusinessHours: true,
          pausesOn: [],
        },
      ],
      escalation: [],
      countAiAsFirstResponse: true,
    };
  }
  return {
    name: policy.name,
    description: policy.description ?? '',
    entityType: policy.entityType,
    status: policy.status,
    scope: policy.scope ?? {},
    targets: policy.targets.map((row) => ({ ...row, pausesOn: [...row.pausesOn] })),
    escalation: (policy.escalation ?? []).map((row) => ({
      ...row,
      notify: { ...row.notify, teamIds: [...row.notify.teamIds] },
    })),
    countAiAsFirstResponse: policy.countAiAsFirstResponse,
  };
}

/**
 * Escopo LIMPO dos predicados que não existem na entidade da política.
 *
 * `pipelineIds` e `stageIds` são conceitos de CHAMADO. Em política de conversa
 * eles não são só inúteis: são LETAIS e silenciosos. `matchesScope`
 * (`shared/sla/resolve.ts`) trata predicado preenchido contra item sem o campo
 * como NÃO-casamento (`matchesList(lista cheia, undefined) === false`), e o
 * subject de conversa nunca tem `pipelineId`/`stageId` — a política salva
 * assim jamais casa, sem erro no salvamento e sem nada na tela que denuncie
 * (a aba Escopo ESCONDE o campo de funis quando a entidade é conversa, então
 * o valor herdado fica invisível e não há como limpá-lo pela UI).
 *
 * O que a aba Escopo continua MOSTRANDO em conversa — clientes, canais,
 * prioridades — não é tocado aqui: campo visível é responsabilidade do
 * usuário, e canal é justamente o eixo principal da política de conversa.
 *
 * Aplicado em DOIS pontos, de propósito (achado da revisão final ampla,
 * 15/08): na troca do Select "Aplica-se a" (`SlaPolicyDrawer`, limpa o
 * rascunho na hora, que é o que o usuário vê) e na montagem dos requests
 * (defesa em profundidade — pega também política de conversa legada que já
 * tenha o campo fantasma gravado: o primeiro save pela tela a limpa).
 */
export function scopeForEntityType(
  scope: SlaPolicyScope,
  entityType: SlaEntityType,
): SlaPolicyScope {
  if (entityType !== 'conversation') return scope;
  const { pipelineIds: _pipelineIds, stageIds: _stageIds, ...rest } = scope;
  return rest;
}

/** `status` NÃO entra no create (só existe em `UpdateSlaPolicyRequest`). */
export function draftToCreateRequest(draft: SlaPolicyDraft): CreateSlaPolicyRequest {
  return {
    name: draft.name.trim(),
    ...(draft.description.trim() ? { description: draft.description.trim() } : {}),
    entityType: draft.entityType,
    scope: scopeForEntityType(draft.scope, draft.entityType),
    targets: draft.targets,
    escalation: draft.escalation,
    countAiAsFirstResponse: draft.countAiAsFirstResponse,
  };
}

/**
 * `entityType` NÃO entra no update: trocá-lo reabriria relógios de outra
 * coleção (B1).
 *
 * `description` viaja SEMPRE, inclusive vazia. O `updateSlaPolicySchema` é
 * `.partial().strict()` — campo ausente significa "não mexe" —, então omitir
 * a string vazia (como o create faz, onde não há nada a limpar) tornava
 * IMPOSSÍVEL apagar a descrição de uma política existente: o salvamento
 * confirmava com toast de sucesso e a descrição antiga reaparecia ao
 * reabrir. O Zod aceita `''` (`z.string().max(1000)`, sem `.min`), e o
 * repository grava/devolve a string vazia sem tratamento especial.
 */
export function draftToUpdateRequest(draft: SlaPolicyDraft): UpdateSlaPolicyRequest {
  return {
    name: draft.name.trim(),
    description: draft.description.trim(),
    status: draft.status,
    scope: scopeForEntityType(draft.scope, draft.entityType),
    targets: draft.targets,
    escalation: draft.escalation,
    countAiAsFirstResponse: draft.countAiAsFirstResponse,
  };
}

/** Primeira mensagem de erro do rascunho inteiro, ou `null` se pode salvar. */
export function validatePolicyDraft(draft: SlaPolicyDraft): string | null {
  if (!draft.name.trim()) return 'Dê um nome à política.';
  if (draft.name.trim().length > 200) return 'O nome tem no máximo 200 caracteres.';
  if (draft.targets.length === 0) return 'A política precisa de pelo menos uma linha de alvo.';
  if (draft.targets.length > 40) return 'No máximo 40 linhas de alvo.';
  if (draft.escalation.length > 20) return 'No máximo 20 linhas de escalação.';

  for (const row of draft.targets) {
    const error = validateTargetRow(row, draft.entityType);
    if (error) return error;
  }

  const dupTarget = findDuplicateTargetIndex(draft.targets);
  if (dupTarget !== -1) {
    const row = draft.targets[dupTarget]!;
    return `Linha de alvo duplicada: ${CLOCK_KEY_LABELS[row.clockKey]} · ${PRIORITY_MATCH_LABELS[row.priority]}.`;
  }

  const dupEscalation = findDuplicateEscalationIndex(draft.escalation);
  if (dupEscalation !== -1) {
    const row = draft.escalation[dupEscalation]!;
    return `Nível de escalação duplicado: ${CLOCK_KEY_LABELS[row.clockKey]} · ${ESCALATION_LEVEL_LABELS[row.level]}.`;
  }

  return null;
}
